import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { RefreshCw, ShoppingBag, Wrench, Handshake, Mail, Images, Trash2, Loader2, UploadCloud, X, Pencil, LogOut, Flag, Package } from "lucide-react";
import { PageWrap } from "@/components/PageWrap";
import { PinGate } from "@/components/admin/PinGate";
import { RaceManager } from "@/components/admin/RaceManager";
import { ProductManager } from "@/components/admin/ProductManager";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  getAdminOverview,
  getAdminOrders,
  getAdminWorkshopInquiries,
  getAdminPartnershipInquiries,
  getAdminContactMessages,
  getGallery,
  uploadGalleryPhoto,
  updateGalleryPhoto,
  deleteGalleryPhoto,
  resolveImageUrl,
  getAdminToken,
  clearAdminToken,
} from "@/lib/api";

const czk = (n) => `${n.toLocaleString("cs-CZ")} Kč`;
const fmtDate = (iso) =>
  new Date(iso).toLocaleString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_SIZE = 8 * 1024 * 1024;

export default function Admin() {
  const [authed, setAuthed] = useState(() => !!getAdminToken());
  const [overview, setOverview] = useState({ orders: 0, workshop_inquiries: 0, partnership_inquiries: 0, contact_messages: 0, gallery_photos: 0, races: 0, products: 0 });
  const [orders, setOrders] = useState([]);
  const [workshop, setWorkshop] = useState([]);
  const [partnership, setPartnership] = useState([]);
  const [messages, setMessages] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);

  // gallery upload form state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoTitle, setPhotoTitle] = useState("");
  const [photoDescription, setPhotoDescription] = useState("");
  const [photoSubmitting, setPhotoSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // edit dialog state
  const [editPhoto, setEditPhoto] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, o, w, p, m, g] = await Promise.all([
        getAdminOverview(),
        getAdminOrders(),
        getAdminWorkshopInquiries(),
        getAdminPartnershipInquiries(),
        getAdminContactMessages(),
        getGallery(),
      ]);
      setOverview(ov);
      setOrders(o);
      setWorkshop(w);
      setPartnership(p);
      setMessages(m);
      setGallery(g);
    } catch {
      // fail silently, tables show empty states
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) loadAll();
  }, [authed, loadAll]);

  // session expired / invalid token → back to PIN gate
  useEffect(() => {
    const onUnauthorized = () => {
      setAuthed(false);
      toast.error("Přístup vypršel. Zadej PIN znovu.");
    };
    window.addEventListener("driftex-admin-unauthorized", onUnauthorized);
    return () => window.removeEventListener("driftex-admin-unauthorized", onUnauthorized);
  }, []);

  const logout = () => {
    clearAdminToken();
    setAuthed(false);
    toast.success("Odhlášeno z pit wall.");
  };

  // revoke object URL on change/unmount
  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const refreshOverview = async () => {
    try {
      setOverview(await getAdminOverview());
    } catch {
      // ignore
    }
  };

  const refreshGallery = async () => {
    try {
      const [g, ov] = await Promise.all([getGallery(), getAdminOverview()]);
      setGallery(g);
      setOverview(ov);
    } catch {
      // ignore
    }
  };

  /* ---------- file selection ---------- */

  const acceptFile = (file) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Nepodporovaný formát. Povolené: JPG, PNG, WEBP, GIF.");
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error("Soubor je příliš velký (max 8 MB).");
      return;
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };

  const clearFile = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /* ---------- submit upload ---------- */

  const submitPhoto = async (e) => {
    e.preventDefault();
    if (!photoFile) {
      toast.error("Vyber nejdřív fotku.");
      return;
    }
    if (!photoTitle.trim()) {
      toast.error("Vyplň název fotky.");
      return;
    }
    setPhotoSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", photoFile);
      fd.append("title", photoTitle.trim());
      fd.append("description", photoDescription.trim());
      await uploadGalleryPhoto(fd);
      toast.success("Fotka nahrána do galerie!");
      clearFile();
      setPhotoTitle("");
      setPhotoDescription("");
      await refreshGallery();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Fotku se nepodařilo nahrát.");
    } finally {
      setPhotoSubmitting(false);
    }
  };

  /* ---------- edit ---------- */

  const openEdit = (photo) => {
    setEditPhoto(photo);
    setEditTitle(photo.title);
    setEditDescription(photo.description || "");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editTitle.trim()) {
      toast.error("Název nesmí být prázdný.");
      return;
    }
    setEditSaving(true);
    try {
      await updateGalleryPhoto(editPhoto.id, { title: editTitle.trim(), description: editDescription.trim() });
      toast.success("Změny uloženy.");
      setEditPhoto(null);
      await refreshGallery();
    } catch {
      toast.error("Změny se nepodařilo uložit.");
    } finally {
      setEditSaving(false);
    }
  };

  const removePhoto = async (photoId) => {
    try {
      await deleteGalleryPhoto(photoId);
      toast.success("Fotka smazána.");
      await refreshGallery();
    } catch {
      toast.error("Fotku se nepodařilo smazat.");
    }
  };

  const CARDS = [
    { icon: ShoppingBag, label: "Objednávky", value: overview.orders, id: "orders" },
    { icon: Wrench, label: "Poptávky dílna", value: overview.workshop_inquiries, id: "workshop" },
    { icon: Handshake, label: "Spolupráce", value: overview.partnership_inquiries, id: "partnership" },
    { icon: Mail, label: "Zprávy", value: overview.contact_messages, id: "messages" },
    { icon: Images, label: "Fotky v galerii", value: overview.gallery_photos, id: "gallery" },
    { icon: Flag, label: "Závody", value: overview.races, id: "races" },
    { icon: Package, label: "Produkty", value: overview.products, id: "products" },
  ];

  if (!authed) {
    return (
      <PageWrap testId="admin-page">
        <PinGate onSuccess={() => setAuthed(true)} />
      </PageWrap>
    );
  }

  return (
    <PageWrap testId="admin-page">
      <section className="mx-auto max-w-6xl px-4 pt-32 pb-20 sm:px-6 lg:px-8">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B7FF2A]">// Pit wall</div>
            <h1 data-testid="admin-headline" className="mt-2 font-display text-5xl uppercase tracking-[0.04em] sm:text-7xl">
              Admin
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-refresh-button"
              onClick={loadAll}
              disabled={loading}
              variant="outline"
              className="clip-corner-sm border-white/20 font-display uppercase tracking-[0.14em] hover:bg-white/5"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Obnovit
            </Button>
            <Button
              data-testid="admin-logout-button"
              onClick={logout}
              variant="outline"
              className="clip-corner-sm border-white/20 font-display uppercase tracking-[0.14em] text-white/70 hover:bg-white/5 hover:text-[#FF3B3B]"
            >
              <LogOut className="mr-2 h-4 w-4" /> Odhlásit
            </Button>
          </div>
        </div>

        {/* overview cards */}
        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {CARDS.map((c, i) => (
            <motion.div
              key={c.id}
              data-testid={`admin-overview-card-${c.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.06 * i }}
              className="clip-corner-sm border border-white/10 bg-[#0F1217] p-5"
            >
              <c.icon className="h-5 w-5 text-[#1EA7FF]" />
              <div className="mt-3 font-mono text-3xl font-semibold">{c.value}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.14em] text-white/45">{c.label}</div>
            </motion.div>
          ))}
        </div>

        {/* tabs */}
        <Tabs defaultValue="orders" className="mt-10">
          <TabsList data-testid="admin-tabs" className="h-auto flex-wrap gap-1 border border-white/10 bg-[#0F1217] p-1">
            <TabsTrigger data-testid="admin-tab-orders" value="orders" className="font-display uppercase tracking-[0.1em] data-[state=active]:bg-[#1EA7FF] data-[state=active]:text-[#07080A]">
              Objednávky
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-workshop" value="workshop" className="font-display uppercase tracking-[0.1em] data-[state=active]:bg-[#1EA7FF] data-[state=active]:text-[#07080A]">
              Dílna
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-partnership" value="partnership" className="font-display uppercase tracking-[0.1em] data-[state=active]:bg-[#1EA7FF] data-[state=active]:text-[#07080A]">
              Spolupráce
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-messages" value="messages" className="font-display uppercase tracking-[0.1em] data-[state=active]:bg-[#1EA7FF] data-[state=active]:text-[#07080A]">
              Zprávy
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-gallery" value="gallery" className="font-display uppercase tracking-[0.1em] data-[state=active]:bg-[#B7FF2A] data-[state=active]:text-[#07080A]">
              Správa Galerie
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-races" value="races" className="font-display uppercase tracking-[0.1em] data-[state=active]:bg-[#B7FF2A] data-[state=active]:text-[#07080A]">
              Správa Závodů
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-products" value="products" className="font-display uppercase tracking-[0.1em] data-[state=active]:bg-[#B7FF2A] data-[state=active]:text-[#07080A]">
              Správa Shopu
            </TabsTrigger>
          </TabsList>

          {/* orders */}
          <TabsContent value="orders" className="mt-4">
            <div className="clip-corner-sm overflow-x-auto border border-white/10 bg-[#0F1217]">
              <Table data-testid="admin-orders-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Číslo</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Zákazník</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Položky</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Celkem</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Datum</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="py-10 text-center text-white/40" data-testid="admin-orders-empty">
                        Zatím žádné objednávky.
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((o) => (
                      <TableRow key={o.id} className="border-white/5" data-testid={`admin-order-row-${o.order_number}`}>
                        <TableCell className="font-mono text-[#B7FF2A]">{o.order_number}</TableCell>
                        <TableCell>
                          <div className="text-sm">{o.customer_name}</div>
                          <div className="text-xs text-white/45">{o.email}</div>
                          <div className="text-xs text-white/35">{o.street}, {o.city} {o.zip_code}</div>
                        </TableCell>
                        <TableCell>
                          {o.items.map((it, idx) => (
                            <div key={idx} className="text-xs text-white/60">
                              {it.qty}× {it.name}{it.size ? ` (${it.size})` : ""}
                            </div>
                          ))}
                        </TableCell>
                        <TableCell className="font-mono text-sm font-semibold">{czk(o.total)}</TableCell>
                        <TableCell className="font-mono text-xs text-white/45">{fmtDate(o.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* workshop */}
          <TabsContent value="workshop" className="mt-4">
            <div className="clip-corner-sm overflow-x-auto border border-white/10 bg-[#0F1217]">
              <Table data-testid="admin-workshop-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Kontakt</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Auto</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Projekt</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Rozpočet</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Popis</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Datum</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workshop.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-10 text-center text-white/40" data-testid="admin-workshop-empty">
                        Zatím žádné poptávky.
                      </TableCell>
                    </TableRow>
                  ) : (
                    workshop.map((w) => (
                      <TableRow key={w.id} className="border-white/5">
                        <TableCell>
                          <div className="text-sm">{w.name}</div>
                          <div className="text-xs text-white/45">{w.email}</div>
                          {w.phone && <div className="text-xs text-white/35">{w.phone}</div>}
                        </TableCell>
                        <TableCell className="text-sm">{w.car_model}</TableCell>
                        <TableCell>
                          <Badge className="rounded-sm border border-[#1EA7FF]/40 bg-[#1EA7FF]/10 font-mono text-[10px] text-[#1EA7FF] hover:bg-[#1EA7FF]/10">
                            {w.project_type}{w.project_type === "Jiné" && w.project_type_other ? `: ${w.project_type_other}` : ""}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{w.budget}</TableCell>
                        <TableCell className="max-w-56"><div className="line-clamp-3 text-xs text-white/60">{w.details}</div></TableCell>
                        <TableCell className="font-mono text-xs text-white/45">{fmtDate(w.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* partnership */}
          <TabsContent value="partnership" className="mt-4">
            <div className="clip-corner-sm overflow-x-auto border border-white/10 bg-[#0F1217]">
              <Table data-testid="admin-partnership-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Firma</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Kontakt</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Typ</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Rozpočet</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Zpráva</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Datum</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partnership.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-10 text-center text-white/40" data-testid="admin-partnership-empty">
                        Zatím žádné nabídky.
                      </TableCell>
                    </TableRow>
                  ) : (
                    partnership.map((p) => (
                      <TableRow key={p.id} className="border-white/5">
                        <TableCell className="text-sm font-medium">{p.company}</TableCell>
                        <TableCell>
                          <div className="text-sm">{p.name}</div>
                          <div className="text-xs text-white/45">{p.email}</div>
                        </TableCell>
                        <TableCell>
                          <Badge className="rounded-sm border border-[#B7FF2A]/40 bg-[#B7FF2A]/10 font-mono text-[10px] text-[#B7FF2A] hover:bg-[#B7FF2A]/10">
                            {p.partnership_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{p.budget || "—"}</TableCell>
                        <TableCell className="max-w-56"><div className="line-clamp-3 text-xs text-white/60">{p.message}</div></TableCell>
                        <TableCell className="font-mono text-xs text-white/45">{fmtDate(p.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* messages */}
          <TabsContent value="messages" className="mt-4">
            <div className="clip-corner-sm overflow-x-auto border border-white/10 bg-[#0F1217]">
              <Table data-testid="admin-messages-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Jméno</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">E-mail</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Zpráva</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-[0.14em]">Datum</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {messages.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-10 text-center text-white/40" data-testid="admin-messages-empty">
                        Zatím žádné zprávy.
                      </TableCell>
                    </TableRow>
                  ) : (
                    messages.map((m) => (
                      <TableRow key={m.id} className="border-white/5">
                        <TableCell className="text-sm">{m.name}</TableCell>
                        <TableCell className="text-xs text-white/50">{m.email}</TableCell>
                        <TableCell className="max-w-72"><div className="line-clamp-3 text-xs text-white/60">{m.message}</div></TableCell>
                        <TableCell className="font-mono text-xs text-white/45">{fmtDate(m.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* gallery management */}
          <TabsContent value="gallery" className="mt-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
              {/* upload form */}
              <div className="lg:col-span-5">
                <form
                  data-testid="admin-gallery-form"
                  onSubmit={submitPhoto}
                  className="clip-corner space-y-4 border border-white/10 bg-[#0F1217] p-6"
                >
                  <div className="flex items-center gap-3">
                    <div className="clip-corner-sm flex h-10 w-10 items-center justify-center bg-[#B7FF2A]/10 text-[#B7FF2A]">
                      <UploadCloud className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-display text-xl uppercase tracking-[0.06em]">Nahrát fotku</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                        Nová fotka se hned zobrazí v galerii
                      </div>
                    </div>
                  </div>

                  {/* drag & drop zone / preview */}
                  {photoPreview ? (
                    <div className="clip-corner-sm relative overflow-hidden border border-[#B7FF2A]/30">
                      <img
                        data-testid="admin-gallery-preview"
                        src={photoPreview}
                        alt="Náhled"
                        className="aspect-video w-full object-cover"
                      />
                      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-[#07080A]/80 px-3 py-2">
                        <span className="truncate font-mono text-[11px] text-white/70">
                          {photoFile?.name} · {(photoFile?.size / 1024 / 1024).toFixed(1)} MB
                        </span>
                        <button
                          type="button"
                          data-testid="admin-gallery-preview-remove"
                          onClick={clearFile}
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white/5 text-white/60 transition-colors hover:bg-[#FF3B3B] hover:text-white"
                          aria-label="Odebrat soubor"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      data-testid="admin-gallery-dropzone"
                      role="button"
                      tabIndex={0}
                      onClick={() => fileInputRef.current?.click()}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click(); }}
                      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                      onDragLeave={() => setDragActive(false)}
                      onDrop={onDrop}
                      className={`clip-corner-sm flex cursor-pointer flex-col items-center justify-center gap-3 border-2 border-dashed px-6 py-10 text-center transition-colors duration-200 ${
                        dragActive
                          ? "border-[#B7FF2A] bg-[#B7FF2A]/10"
                          : "border-white/15 bg-[#0B0D10] hover:border-[#B7FF2A]/50 hover:bg-[#B7FF2A]/5"
                      }`}
                    >
                      <UploadCloud className={`h-9 w-9 ${dragActive ? "text-[#B7FF2A]" : "text-white/30"}`} />
                      <div>
                        <div className="font-display text-lg uppercase tracking-[0.08em] text-white/80">
                          Přetáhni fotku sem
                        </div>
                        <div className="mt-1 font-mono text-[11px] text-white/40">
                          nebo klikni pro výběr souboru · JPG / PNG / WEBP / GIF · max 8 MB
                        </div>
                      </div>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    data-testid="admin-gallery-file-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => acceptFile(e.target.files?.[0])}
                  />

                  <Input
                    data-testid="admin-gallery-title-input"
                    placeholder="Název fotky *"
                    value={photoTitle}
                    onChange={(e) => setPhotoTitle(e.target.value)}
                    className="h-11 border-white/15 bg-[#0B0D10]"
                  />
                  <Input
                    data-testid="admin-gallery-description-input"
                    placeholder="Krátký popis"
                    value={photoDescription}
                    onChange={(e) => setPhotoDescription(e.target.value)}
                    className="h-11 border-white/15 bg-[#0B0D10]"
                  />

                  <Button
                    type="submit"
                    disabled={photoSubmitting}
                    data-testid="admin-gallery-submit-button"
                    className="light-sweep clip-corner-sm h-12 w-full bg-[#B7FF2A] font-display text-base uppercase tracking-[0.14em] text-[#07080A] hover:bg-[#B7FF2A] hover:brightness-110 disabled:opacity-60"
                  >
                    {photoSubmitting ? (
                      <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Nahrávám…</span>
                    ) : (
                      "Nahrát do galerie"
                    )}
                  </Button>
                </form>
              </div>

              {/* photo list */}
              <div className="lg:col-span-7">
                <div data-testid="admin-gallery-list" className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {gallery.length === 0 ? (
                    <div className="col-span-full py-14 text-center text-white/40" data-testid="admin-gallery-empty">
                      Galerie je prázdná. Nahraj první fotku.
                    </div>
                  ) : (
                    gallery.map((g) => (
                      <div
                        key={g.id}
                        data-testid={`admin-gallery-photo-${g.id}`}
                        className="clip-corner-sm group relative overflow-hidden border border-white/10 bg-[#0F1217]"
                      >
                        <img src={resolveImageUrl(g.url)} alt={g.title} className="aspect-square w-full object-cover" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#07080A]/90 via-transparent to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <div className="truncate font-display text-sm uppercase tracking-[0.06em] text-white">{g.title}</div>
                          {g.description && (
                            <div className="truncate font-display text-[11px] uppercase tracking-[0.06em] text-white/60">
                              {g.description}
                            </div>
                          )}
                        </div>
                        <div className="absolute right-2 top-2 flex gap-1.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                          <button
                            data-testid={`admin-gallery-edit-${g.id}`}
                            onClick={() => openEdit(g)}
                            className="flex h-8 w-8 items-center justify-center rounded-md bg-[#07080A]/70 text-white/60 transition-colors duration-200 hover:bg-[#1EA7FF] hover:text-[#07080A]"
                            aria-label="Upravit fotku"
                            title="Upravit"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            data-testid={`admin-gallery-delete-${g.id}`}
                            onClick={() => removePhoto(g.id)}
                            className="flex h-8 w-8 items-center justify-center rounded-md bg-[#07080A]/70 text-white/60 transition-colors duration-200 hover:bg-[#FF3B3B] hover:text-white"
                            aria-label="Smazat fotku"
                            title="Smazat"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* race calendar management */}
          <TabsContent value="races" className="mt-4">
            <RaceManager onChanged={refreshOverview} />
          </TabsContent>

          {/* shop products management */}
          <TabsContent value="products" className="mt-4">
            <ProductManager onChanged={refreshOverview} />
          </TabsContent>
        </Tabs>
      </section>

      {/* edit dialog */}
      <Dialog open={!!editPhoto} onOpenChange={(open) => !open && setEditPhoto(null)}>
        <DialogContent
          data-testid="admin-gallery-edit-dialog"
          aria-describedby={undefined}
          className="max-w-md border-white/10 bg-[#0F1217]"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-2xl uppercase tracking-[0.08em] text-white">
              <Pencil className="h-5 w-5 text-[#1EA7FF]" /> Upravit fotku
            </DialogTitle>
          </DialogHeader>
          {editPhoto && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="clip-corner-sm overflow-hidden border border-white/10">
                <img src={resolveImageUrl(editPhoto.url)} alt={editPhoto.title} className="aspect-video w-full object-cover" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Název</label>
                <Input
                  data-testid="admin-gallery-edit-title-input"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="mt-1.5 h-11 border-white/15 bg-[#0B0D10]"
                  placeholder="Název fotky *"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Krátký popis</label>
                <Input
                  data-testid="admin-gallery-edit-description-input"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="mt-1.5 h-11 border-white/15 bg-[#0B0D10]"
                  placeholder="Krátký popis"
                />
              </div>
              <div className="flex gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="admin-gallery-edit-cancel-button"
                  onClick={() => setEditPhoto(null)}
                  className="clip-corner-sm flex-1 border-white/20 font-display uppercase tracking-[0.12em] hover:bg-white/5"
                >
                  Zrušit
                </Button>
                <Button
                  type="submit"
                  disabled={editSaving}
                  data-testid="admin-gallery-edit-save-button"
                  className="light-sweep clip-corner-sm flex-1 bg-[#1EA7FF] font-display uppercase tracking-[0.12em] text-[#07080A] hover:bg-[#1EA7FF] hover:brightness-110 disabled:opacity-60"
                >
                  {editSaving ? (
                    <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Ukládám…</span>
                  ) : (
                    "Uložit změny"
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </PageWrap>
  );
}
